// Libraries
import UserAvatar from "@/components/Avatar/UserAvatar";
import Image from "next/image";
import { Link } from "@/i18n/navigation";
import { capitalize } from "@/helpers/capitalize";
import moment from "moment-timezone";
import { useLocale, useTranslations } from "next-intl";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";

/**
 * Carte d'un événement à venir (tableau de bord) : un direct ou un appel de
 * mentorat. Pastille de date, type, titre, horaire et durée, statut « En
 * cours » quand il a commencé, puis la photo de la personne (appel) ou un
 * objet pastel des pages accélérateurs (direct).
 */
export default function BookedEvent({ event, session }) {
    const locale = useLocale();
    const t = useTranslations();

    const isLive = Boolean(event.event);
    const tz = session.user.timezone || "Europe/Paris";

    // Appel de prospection : pas de mentee (User), un guest (Contact) à la place.
    // Sans ce garde, le dashboard d'un mentor plantait sur un appel invité.
    const attendee = event.mentee || event.guest || {};
    const attendeeName =
        attendee.pseudo && attendee.showPseudo
            ? capitalize(attendee.pseudo)
            : `${capitalize(attendee.firstName || "")} ${capitalize(
                  attendee.lastName || "",
              )}`.trim();
    const isMentor = !isLive && event.mentor?.id == session.user.userId;
    const other = isLive ? null : isMentor ? attendee : event.mentor;
    const otherName = isLive
        ? null
        : isMentor
          ? attendeeName
          : `${capitalize(event.mentor.firstName)} ${capitalize(
                event.mentor.lastName,
            )}`;

    const start = moment
        .utc(isLive ? event.eventDate.date : event.plannedDate)
        .tz(tz)
        .locale(locale);
    const durationMinutes = isLive
        ? parseInt(event.event.duration, 10) || 60
        : Number(event.minutesPerCall) || 0;
    const now = moment();
    const started = now.isSameOrAfter(start);
    const inProgress =
        started && now.isBefore(start.clone().add(durationMinutes, "minutes"));
    const isToday = start.isSame(now, "day");

    // Majuscule en tête seulement : capitalize() en mettrait une au mois.
    const upperFirst = (text) => text.charAt(0).toUpperCase() + text.slice(1);
    const when = upperFirst(
        start.calendar(null, {
            sameDay: t("BookedEvent_today", { date: "HH:mm" }),
            nextDay: t("BookedEvent_tomorrow", { date: "HH:mm" }),
            nextWeek: t("BookedEvent_nextWeek", {
                day: "dddd",
                hour: "HH:mm",
            }),
            lastDay: t("BookedEvent_yesterday", { date: "HH:mm" }),
            lastWeek: "LLL",
            sameElse: "LLL",
        }),
    );

    return (
        <Link
            href={
                isLive
                    ? `/events/${event.event.slug}/private-${event.eventDate.id}`
                    : `/call/${event.token}`
            }
            aria-label={t("BookedEvent_join")}
            className="group flex h-full items-center gap-4 rounded-2xl border border-gray-200/70 dark:border-borderDark bg-white dark:bg-bgDark-840 p-4 shadow-sm transition-all hover:border-gray-300 hover:shadow-md dark:hover:border-grayDark-600"
        >
            {/* Pastille de date */}
            <div
                className={`flex w-14 flex-shrink-0 flex-col items-center rounded-xl border py-2 ${
                    isToday
                        ? "border-primary/30 bg-primary/5"
                        : "border-gray-200/70 dark:border-borderDark bg-gray-50 dark:bg-bgDark-900"
                }`}
            >
                <span
                    className={`text-[10px] font-semibold uppercase tracking-wider ${
                        isToday
                            ? "text-primary"
                            : "text-gray-500 dark:text-grayDark-400"
                    }`}
                >
                    {start.format("MMM").replace(".", "")}
                </span>
                <span className="text-xl font-semibold leading-tight tabular-nums text-gray-900 dark:text-white">
                    {start.format("D")}
                </span>
                {!start.isSame(now, "year") && (
                    <span className="text-[10px] tabular-nums text-gray-400 dark:text-grayDark-500">
                        {start.format("YYYY")}
                    </span>
                )}
            </div>

            {/* Contenu */}
            <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                    <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-gray-400 dark:text-grayDark-500">
                        <SolarIcon
                            name={
                                isLive
                                    ? "videocamera-record-linear"
                                    : "chat-round-call-linear"
                            }
                            className="text-sm"
                        />
                        {isLive ? t("BookedEvent_live") : t("BookedEvent_call")}
                    </span>
                    {inProgress && (
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300">
                            <span className="relative flex h-1.5 w-1.5">
                                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-500 opacity-75"></span>
                                <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500"></span>
                            </span>
                            {t("BookedEvent_in_progress")}
                        </span>
                    )}
                </div>
                <div className="mt-1 line-clamp-2 text-sm font-semibold leading-snug text-gray-900 dark:text-white">
                    {isLive
                        ? event.event.title
                        : t("BookedEvent_call_with", { fullname: otherName })}
                </div>
                <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-gray-500 dark:text-grayDark-400">
                    <span className="inline-flex items-center gap-1">
                        <SolarIcon
                            name="calendar-linear"
                            className="text-sm"
                        />
                        {when}
                    </span>
                    <span className="inline-flex items-center gap-1">
                        <SolarIcon
                            name="clock-circle-linear"
                            className="text-sm"
                        />
                        {isLive
                            ? event.event.duration
                            : t("BookedEvent_minutesPerCall", {
                                  minutesPerCall: event.minutesPerCall,
                              })}
                    </span>
                </div>
            </div>

            {/* Personne ou objet pastel, puis flèche */}
            <div className="flex flex-shrink-0 items-center gap-2">
                {other?.photo ? (
                    <UserAvatar
                        preferPhoto
                        user={other}
                        alt=""
                        width={40}
                        height={40}
                        className="hidden h-10 w-10 rounded-full object-cover ring-2 ring-white dark:ring-bgDark-840 sm:block"
                    />
                ) : (
                    <Image
                        src={
                            isLive
                                ? "/images/programmes/rocket/hero/fusee.webp"
                                : "/images/programmes/python/hero/calendrier.webp"
                        }
                        alt=""
                        width={40}
                        height={40}
                        className="hidden h-10 w-10 object-contain sm:block"
                    />
                )}
                <SolarIcon
                    name="alt-arrow-right-linear"
                    className="text-lg text-gray-300 transition-transform group-hover:translate-x-0.5 group-hover:text-gray-500 dark:text-grayDark-600"
                />
            </div>
        </Link>
    );
}
